import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'LuxKit/Button/TextButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import BodyText from 'LuxKit/Typography/BodyText'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import Action from '../../Chat/MessageMeta/Action'
import LesaPurchaseSummaryMetaContainer from './Common/LesaPurchaseSummaryMetaContainer'
import { filterActions } from '../../../utils/actionsUtils'
import Modal from 'LuxKit/Modal/Modal'
import useToggle from '../../../../client/hooks/useToggle'
import BedbankPropertyFinePrint from 'components/Common/BedbankPropertyFinePrint'
import { canChangeBedbankDates, getBedbankItems } from 'supportAssistant/utils/orderUtils'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { buildChatMessageMetaPayload } from 'supportAssistant/utils/messageUtils'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { fetchOffersById } from 'actions/OfferActions'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { canChangeBedbankGuestName, canShowBedbankSpecialRequestsTile, isActiveBedbankGuestNameChangeBooking } from 'components/Common/OrderView/OrderItemSummaries/BedbankItemSummary/BedbankRoomSummaryUtils'

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  /**
   * Replaces this card's own selectOrder dispatch. Set when the card stands in for an assistant
   * booking-selection pill, which posts the pill's own value back instead of a chat action.
   */
  onSelect?: () => void
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}
export default function LesaBedbankPurchaseSummary(props: Props) {
  const intl = useIntl()
  // verify how to render bookingInfo as we can have few rooms
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, isCurrentOrder, canEdit, onSelect: onSelectOverride } = props

  const dispatch = useAppDispatch()

  const items = getBedbankItems(order)
  const item = items[0]
  const isSelfServiceNameChangeEnabled = useFeatureFlag(OptimizelyFeatureFlags.opexSelfServiceNameChange)
  const nameChangeRoom = useMemo(() => {
    if (!item) return undefined
    return item.rooms.find(room => isActiveBedbankGuestNameChangeBooking(item, room)) ?? item.rooms[0]
  }, [item])

  const roomId = nameChangeRoom?.roomId
  const [cancellationConfig] = useOrderCancellationConfigs(roomId, order.id)

  const experienceItems = order.experienceItems.length

  const offer = item?.offer
  const [liveOffer] = useOffer<App.Offer | App.BedbankOffer>(offer.id)

  const location = offer?.property?.address
    ? (offer.property.address?.city && offer.property.address?.stateProvinceName
        ? intl.formatMessage(
            {
              id: 'fwkB86LvwJIKf2A0Lvt-',
              defaultMessage: '{city}, {stateProvince}',
              description: 'Support assistant bedbank purchase summary city and state/province separated by comma',
            },
            { city: offer.property.address.city, stateProvince: offer.property.address.stateProvinceName },
          )
        : (offer.property.address?.city || offer.property.address?.stateProvinceName))
    : undefined
  const canChangeDates = !!liveOffer && canChangeBedbankDates(order, cancellationConfig)
  const canBeCancelled = cancellationConfig.policyType !== 'non-refundable' && cancellationConfig.canRefund
  const canChangeGuestName = Boolean(item && nameChangeRoom && canChangeBedbankGuestName({
    isSelfServiceNameChangeEnabled,
    canEdit,
    item,
    room: nameChangeRoom,
    canChangeGuestName: cancellationConfig.canChangeGuestName,
  }))
  // My Escapes' special-request tile rule, so the chat pill shows exactly when My Escapes would.
  const canAddSpecialRequest = Boolean(item?.rooms.some(room => canShowBedbankSpecialRequestsTile(item, room, canEdit)))
  // bedbank inclusions
  const promotions = useBedbankRoomRatePromotions(item.offer.promotions, item.checkIn.format(ISO_DATE_FORMAT), item.checkOut.format(ISO_DATE_FORMAT))

  // The booked package names the promotion the booking was sold under, and that promotion carries the inclusion list.
  // Fall back to the room's own fields when there's no booked promotion.
  const bookedPackage = item?.offer.packages.find(pkg => pkg.roomId === item?.roomTypeId)
  const bookedPromotion = bookedPackage?.rate?.promotionId ? promotions[bookedPackage.rate.promotionId] : undefined
  const ratePromotion =
    bookedPromotion?.inclusions?.length || bookedPromotion?.rateInclusionsLong ? bookedPromotion : undefined
  const roomInclusions = useMemo<Array<App.OfferFeature>>(
    () => (ratePromotion ? [] : [...(item?.rooms[0]?.facilities ?? []), ...(item?.rooms[0]?.promotions ?? [])]),
    [item, ratePromotion],
  )

  const hasInclusions = !!ratePromotion || roomInclusions.length > 0
  const hasExperiences = order.experienceItems.length > 0
  const hasTransfers = order.transferItems.length > 0

  // fine print
  const { off: closeFinePrint, value: isFinePrintOpen } = useToggle(true)

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, !hasInclusions ? ['listInclusions'] : []))

  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', {
      action: isListFlightsOrdersAction ? 'selectFlightOrder' : 'selectOrder',
      payload: {
        ...buildChatMessageMetaPayload(order, item, offer, {
          isCurrentOrder,
          canChangeDates,
          checkInDate: item.checkIn?.format(ISO_DATE_FORMAT),
          canBeCancelled,
          orderType: 'bedbank',
          canAddExtraGuests: false,
          canEditOrder: canEdit,
          canChangeGuestName,
          canAddSpecialRequest,
          roomId,
        }, cancellationConfig, isSelfServiceNameChangeEnabled),
      },
    }))
  }, [dispatch, isListFlightsOrdersAction, order, item, offer, isCurrentOrder, canChangeDates, canBeCancelled, canEdit, cancellationConfig, isSelfServiceNameChangeEnabled, canChangeGuestName, canAddSpecialRequest, roomId])

  useEffect(() => {
    if (hasExperiences || hasTransfers) {
      const experienceIds = order.experienceItems.map(item => item.experienceId)
      const transferIds = order.transferItems.map(item => item.option.experienceId)
      const allExperienceIds = [...experienceIds, ...transferIds]

      dispatch(fetchExperiencesById(...allExperienceIds))
    }

    dispatch(fetchOffersById(order.items.map(item => item.offerId)))
  }, [order, hasExperiences, hasTransfers, dispatch])

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('list-flight-orders') || !!componentsMap?.get('selectFlightOrder') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>{intl.formatMessage(productLabels.hotel)}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'ExGR7R7CFodG9kTuZL6v',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming bedbank hotel booking in the customer support assistant',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'fXSXh-G_jN4ns2LH1Ihm',
              defaultMessage: 'Past',
              description: 'Status label for past bedbank hotel booking in the booking flow',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              {location && <CaptionTextBlock
                variant="medium"
                colour="light-foreground-alt"
              >{location}
              </CaptionTextBlock>}
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {offer?.name}
              </LesaPurchaseSummaryTitle>
              {items.map(item => (
                <LesaPurchaseSummaryItineraryDates
                  key={item.id}
                  status={item.status}
                  startDate={item.checkIn?.format(ISO_DATE_FORMAT)}
                  endDate={item.checkOut?.format(ISO_DATE_FORMAT)}
                  duration={intl.formatMessage({
                    id: 'tiMcVaw2SDhuZimssi0Z',
                    defaultMessage: '{count, plural, one {# night} other {# nights}}',
                    description: 'Number of nights for bedbank hotel stay duration in the customer support assistant',
                  }, { count: item.duration ?? 0 })}
                  showReservationDates
                />
              ))}
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="light-foreground-alt">{formatOccupantsShort(item?.rooms[0]?.occupancy)}</BodyText>
                {!!items.length && <BodyText as="span" variant="small" colour="light-foreground-alt">{intl.formatMessage({
                  id: 'af-jKOj8rmKJD0FijXPA',
                  defaultMessage: '{count, plural, one {# room} other {# rooms}}',
                  description: 'Number of rooms for bedbank hotel booking in the booking flow',
                }, { count: item?.rooms?.length })}
                </BodyText>}
                {!!experienceItems && <BodyText as="span" variant="small" colour="light-foreground-alt">{intl.formatMessage({
                  id: 'xjrEMkNIoZALUhze_KLa',
                  defaultMessage: '{count, plural, one {# experience} other {# experiences}}',
                  description: 'Number of experiences for bedbank hotel booking in the customer support assistant',
                }, { count: experienceItems })}
                </BodyText>}
              </LesaPurchaseSummarySeparator>
            </Group>
            {!hideSelect &&
              <TextButton onClick={onSelectOverride ?? handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'WXdcjsA09tKr_UO4lwML',
                  defaultMessage: 'Select',
                  description: 'Action button label to select a bedbank hotel booking',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get('listInclusions') && (
        <LesaPurchaseSummaryMetaContainer>
          <BodyText variant="medium" weight="bold">{intl.formatMessage({
            id: 'FQlcjhers0zT34iBUW8M',
            defaultMessage: 'Package inclusions:',
            description: 'Bold label for package inclusions section in the inclusions list',
          })}
          </BodyText>
          {ratePromotion?.inclusions?.length ? (
            ratePromotion.inclusions.map(inclusion => (
              <OfferInclusionItem
                key={inclusion.id}
                inclusion={inclusion}
              />
            ))
          ) : (
            !!ratePromotion?.rateInclusionsLong && (
              <MarkdownRender fontSize="medium" type="compact" content={ratePromotion.rateInclusionsLong} />
            )
          )}
          {roomInclusions.map(inclusion => (
            <OfferInclusionItem
              key={inclusion.id}
              inclusion={inclusion}
            />
          ))}
        </LesaPurchaseSummaryMetaContainer>
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <Modal
          isOpen={isFinePrintOpen}
          onClose={closeFinePrint}
          size="L"
          title={intl.formatMessage({
            id: 'Y16-AflTHDKFNSnQCPiq',
            defaultMessage: 'Fine print',
            description: 'Heading title for the fine print modal in the customer support assistant',
          })}
        >
          {offer && <BedbankPropertyFinePrint
            finePrint={offer.property.finePrint!}
          />}
        </Modal>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} order={order} />
          ))}
        </Group>}
    </>
  )
}
